/**
 * The Administration & Security Overview (WALLIYO-0132), built to the approved
 * design.
 *
 * Two questions, and the page is both. Who is waiting to be told yes or no —
 * somebody blocked on a capability is blocked for the rest of their shift — and
 * what nobody took back, because a permanent grant held by somebody who has left
 * is not an oversight, it is a way in.
 *
 * The critical capabilities are counted for the opposite reason: if one of them
 * reaches a single holder, nobody can cover when that person is away.
 */

import type { StaffFeatureId } from "@wallyo/contracts/actions";
import type { AdminOverview as Data, QueueRow } from "@wallyo/contracts/console/overview";

import { featureName, permissionName, t, tCount, type MessageId } from "../../i18n";
import { formatDate, formatNumber } from "../../i18n/format";
import { Figures } from "./OverviewScreen";

export function AdminOverviewView({ data }: { data: Data }) {
  return (
    <>
      <Figures figures={data.figures} />

      <div className="ov-split">
        <section className="card">
          <div className="ov-card-head">
            <h2>{t("overview.administration.askedNotAnswered")}</h2>
            <span className="foot">{t("overview.administration.oldestFirst")}</span>
          </div>
          {data.requests.length === 0 ? (
            <p className="foot">{t("overview.administration.nobodyWaiting")}</p>
          ) : (
            data.requests.map((row) => <Request key={row.reference ?? row.subject} row={row} />)
          )}
        </section>

        <div className="ov-side">
          {data.neverTakenBack ? (
            <section className="card ov-callout">
              <h2>{t("overview.administration.nobodyTookItBack")}</h2>
              <p className="foot">
                {tCount("overview.administration.noEndDate", data.neverTakenBack.count, {
                  count: formatNumber(data.neverTakenBack.count),
                })}{" "}
                {data.neverTakenBack.leavers > 0 ? (
                  <span className="danger">
                    {tCount("overview.administration.belongToLeavers", data.neverTakenBack.leavers, {
                      leavers: formatNumber(data.neverTakenBack.leavers),
                    })}
                  </span>
                ) : null}
              </p>
              <a className="go-link" href="#permissions">
                {t("overview.administration.reviewThem")}
              </a>
            </section>
          ) : null}

          <section className="card">
            <h2>{t("overview.administration.criticalCapabilities")}</h2>
            {data.critical.map((tally) => (
              <div key={tally.tallyId} className="ov-line">
                {/* Named by the capability itself, using the words the Staff
                    features screen uses, so the two never disagree. */}
                <span className="grow">{featureName(tally.tallyId as StaffFeatureId)}</span>
                <span className={tally.count <= 1 ? "mono warn-text" : "mono"}>{formatNumber(tally.count)}</span>
              </div>
            ))}
            <p className="foot">{t("overview.administration.ifAnyReachesOne")}</p>
          </section>
        </div>
      </div>
    </>
  );
}

/**
 * What somebody asked to be allowed to do, in words.
 *
 * An access request can name a permission or a feature, and they live in
 * different catalogues: asking the feature catalogue for a permission gets the
 * id back, which is how "wants to SETTLE_REFUNDS_STAFF_PERMISSION" reached the
 * screen.
 */
const asWords = (id: string): string =>
  id.endsWith("_STAFF_PERMISSION") ? permissionName(id) : featureName(id as StaffFeatureId);

/** Who asked, for what, until when, and how long they have been blocked. */
function Request({ row }: { row: QueueRow }) {
  const asked = row.amount ? asWords(row.amount) : "";
  return (
    <div className="ov-row">
      <span className={`ov-bar-side ${row.tone}`} aria-hidden="true" />
      <span className="grow">
        <span className="ov-row-what">
          {t(`overview.administration.row.${row.rowId}` as MessageId, { subject: row.subject, asked })}
        </span>
        <span
          className={
            row.tone === "bad" ? "ov-row-why danger" : row.tone === "warn" ? "ov-row-why warn-text" : "ov-row-why"
          }
        >
          {row.reference ? <span className="mono ov-ref">{row.reference} · </span> : null}
          {row.on
            ? `${t("overview.administration.until", { on: formatDate(row.on, { day: "numeric", month: "short" }) })} · `
            : `${t("overview.administration.noEndAsked")} · `}
          {tCount("overview.administration.hoursWaiting", row.days ?? 0, { hours: formatNumber(row.days ?? 0) })}
          {(row.days ?? 0) >= 24 ? `, ${t("overview.administration.pastTheDeadline")}` : ""}
        </span>
      </span>
      <a className={row.tone === "bad" ? "btn primary" : "btn"} href={row.href}>
        {t(`overview.action.${row.actionId}` as MessageId)}
      </a>
    </div>
  );
}
